/**
 * Horizontal chip tape for filtering by school. Complements the CategoryChips
 * tape. Uses the exact same visual language: ink-outline pills that flip to
 * ink-filled on active, horizontally scrollable on mobile, flex-wraps on
 * wide viewports.
 *
 * Props:
 *   schools: string[]   — school names that currently have active campaigns
 *   selected: string | null
 *   onSelect: (schoolName | null) => void
 *   className: pass-through wrapper class
 */
const Chip = ({ active, disabled, onClick, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    aria-disabled={disabled || undefined}
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-base font-medium transition-colors ${ active ? "border-ink bg-ink text-sand" : disabled ? "border-border/60 bg-transparent text-olive/50" : "border-border bg-transparent text-ink hover:border-ink hover:bg-bone" }`}
  >
    {children}
  </button>
);

const Count = ({ n, active }) => (
  <span
    className={`ml-0.5 rounded-full px-1.5 text-[13px] font-semibold tabular-nums ${ active ? "bg-sand/25 text-sand" : "bg-olive/12 text-olive" }`}
  >
    {n}
  </span>
);

// Stable, DOM-safe slug for testids.
const slug = (s) =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const SchoolChips = ({ schools = [], selected, onSelect, counts = {}, className = "" }) => {
  if (schools.length === 0) return null;
  const totalCount = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
  return (
    <div
      className={`flex flex-wrap gap-2 overflow-x-auto no-scrollbar pb-1 ${className}`}
      data-testid="school-chips"
      role="group"
      aria-label="Filter campaigns by school"
    >
      <Chip
        active={selected == null}
        onClick={() => onSelect(null)}
        testid="school-chip-all"
      >
        All schools
        <Count n={totalCount} active={selected == null} />
      </Chip>

      {schools.map((name) => {
        const n = counts[name] || 0;
        const active = selected === name;
        return (
          <Chip
            key={name}
            active={active}
            disabled={!active && n === 0}
            onClick={() => onSelect(name)}
            testid={`school-chip-${slug(name)}`}
          >
            {name}
            <Count n={n} active={active} />
          </Chip>
        );
      })}
    </div>
  );
};
